import { RefObject, useCallback, useEffect, useState } from "react";

type TVerticalOrigin = "top" | "center" | "bottom";
type THorizontalOrigin = "left" | "center" | "right";

interface IOrigin {
    vertical: TVerticalOrigin;
    horizontal: THorizontalOrigin;
}

interface IUseFlipPlacementParams {
    anchorRef: RefObject<HTMLElement>;
    isOpen: boolean;
    menuMaxHeight: number;
    anchorGap: number;
    anchorOrigin?: IOrigin;
    transformOrigin?: IOrigin;
}

const DEFAULT_ANCHOR_ORIGIN: IOrigin = { vertical: "bottom", horizontal: "left" };
const DEFAULT_TRANSFORM_ORIGIN: IOrigin = { vertical: "top", horizontal: "left" };

const flipVertical = (vertical: TVerticalOrigin): TVerticalOrigin => {
    if (vertical === "top") {
        return "bottom";
    }
    if (vertical === "bottom") {
        return "top";
    }
    return "center";
};

const flipOrigin = (origin: IOrigin): IOrigin => ({ ...origin, vertical: flipVertical(origin.vertical) });

export const useFlipPlacement = ({
    anchorRef,
    isOpen,
    menuMaxHeight,
    anchorGap,
    anchorOrigin = DEFAULT_ANCHOR_ORIGIN,
    transformOrigin = DEFAULT_TRANSFORM_ORIGIN,
}: IUseFlipPlacementParams) => {
    const [flipped, setFlipped] = useState(false);

    const updateFlipped = useCallback(() => {
        const rect = anchorRef.current?.getBoundingClientRect();
        if (!rect) {
            return;
        }
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        setFlipped(spaceBelow < menuMaxHeight && spaceAbove > spaceBelow);
    }, [anchorRef, menuMaxHeight]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        window.addEventListener("scroll", updateFlipped, true);
        window.addEventListener("resize", updateFlipped);
        return () => {
            window.removeEventListener("scroll", updateFlipped, true);
            window.removeEventListener("resize", updateFlipped);
        };
    }, [isOpen, updateFlipped]);

    return {
        updateFlipped,
        anchorOrigin: flipped ? flipOrigin(anchorOrigin) : anchorOrigin,
        transformOrigin: flipped ? flipOrigin(transformOrigin) : transformOrigin,
        anchorGap: flipped ? -anchorGap : anchorGap,
    };
};
